import React from "react";
import {
	IFormFieldProps,
	EWidgets,
	ICustomFormItemProps
} from "@organisms/ZForm/interface";
import {
	currencyFormatter,
	getCurrencySettings
} from "@/Utils/Currency";
import {
	Distributor,
	distributorLabel,
	ProductBillingType,
	productBillingTypeOptions,
	ProductType,
	ratailPriceLabel
} from "@/Contracts/Products";
import { handleMarkupMargin } from "@/Components/Utils/PricingCalculator";
import ZButton from "@atoms/ZButton";
import ZTypography from "@atoms/ZTypography";
import ZIcon, { Icons } from "@atoms/ZIcon";
import ZInputNumber, {
	InputNumberWidths
} from "@atoms/ZInputNumber";
import { IRenderSellPriceInputGroupProps } from "./interface";
import { EProductDrawerState } from "@pages/Drawers/Products/Product/interface";

const { ZText } = ZTypography;

export default ({
	form,
	getFieldValues,
	bundledProductList,
	setMarginAndAmount,
	currentProductType,
	drawerState,
	retailInfo,
	pricingDisabled,
	isBundleEnhancementEnable
}: IRenderSellPriceInputGroupProps): IFormFieldProps[] => {
	const type =
		drawerState === EProductDrawerState.BUNDLECHILDEDIT
			? currentProductType
			: form.getFieldValue("type");
	const isBundleProduct = type === ProductType.Bundle;

	const onQuantityChange = (newQuantity?: number | string | null): void => {
		if (newQuantity === undefined || newQuantity === null) {
			return;
		}
		const values = getFieldValues();
		form.setFieldsValue(
			handleMarkupMargin(
				values.selected_cost_price,
				values.sell_price,
				Number(newQuantity),
				values.should_add_setup_price_to_margin,
				values.selected_setup_price
			)
		);
		setMarginAndAmount();
	};

	const onSellPriceChange = (newSellPrice?: number | string | null): void => {
		if (newSellPrice === undefined || newSellPrice === null) {
			return;
		}
		const values = getFieldValues();
		const calculatedValues = handleMarkupMargin(
			values.selected_cost_price,
			Number(newSellPrice),
			values.quantity,
			values.should_add_setup_price_to_margin,
			values.selected_setup_price
		);
		
		// Batch all updates in a single call to prevent multiple re-renders that cause focus loss
		form.setFieldsValue({
			...calculatedValues,
			sell_price: Number(newSellPrice)
		});
		setMarginAndAmount();
	};

	const updateSellPriceFromBundleItems = () => {
		const sellPrice =
			bundledProductList?.reduce(
				(total, curr) =>
					curr.is_optional
						? total
						: total + (curr.sell_price || 0) * curr.quantity,
				0
			) || 0;

		form.setFieldsValue({ sell_price: sellPrice });
		setMarginAndAmount();
	};

	const updateSellPriceByRetailPrice = () => {
		if (retailInfo?.price) {
			form.setFieldsValue({ sell_price: retailInfo.price });
			setMarginAndAmount();
		}
	};

	const onSetupPriceChange = (
		newSetupPrice?: number | string | null
	): void => {
		if (newSetupPrice === undefined || newSetupPrice === null) {
			return;
		}
		const values = getFieldValues();
		if (!values.should_add_setup_price_to_margin) {
			return;
		}
		const calculatedValues = handleMarkupMargin(
			values.selected_cost_price,
			values.sell_price,
			values.quantity,
			values.should_add_setup_price_to_margin,
			Number(newSetupPrice)
		);
		
		// Batch all updates in a single call to prevent multiple re-renders that cause focus loss
		form.setFieldsValue({
			...calculatedValues,
			selected_setup_price: Number(newSetupPrice)
		});
		setMarginAndAmount();
	};

	const updateSetupPriceFromBundleItems = () => {
		const totalSetupPrice =
			bundledProductList?.reduce(
				(total, product) =>
					product.is_optional
						? total
						: total + (product.selected_setup_price || 0),
				0
			) || 0;
		form.setFieldsValue({ selected_setup_price: totalSetupPrice });
		setMarginAndAmount();
	};

	const SellPrice = ({ value, onChange: _onChange }: ICustomFormItemProps) => (
		<div>
			<div className="flex items-center">
				<ZInputNumber
					type="number"
					step=".01"
					placeholder="Please enter product sell price"
					min={0}
					className="mr-2"
					width={InputNumberWidths.xl}
					value={value}
					onChange={v => {
						// Don't call onChange(v) here to avoid double form update
						// onSellPriceChange will handle the form update including the input value
						onSellPriceChange(Number(v));
					}}
					precision={2}
					addOnAfter={getCurrencySettings().currency}
					disabled={isBundleEnhancementEnable && pricingDisabled}
				/>
				{form.getFieldValue("unit_of_measure") && (
					<ZText className="ml-2 whitespace-nowrap leading-8">
						{form.getFieldValue("unit_of_measure")}
					</ZText>
				)}
				{isBundleProduct && (
					<ZButton
						className="sub-action-button m-0"
						type="link"
						onClick={updateSellPriceFromBundleItems}
						disabled={isBundleEnhancementEnable && pricingDisabled}
					>
						<ZText className="text-blue-5 active:text-blue-6">
							<ZIcon
								name={Icons.COPY}
								size="sm"
								className="mx-1 stroke-current"
							/>
							Copy Product Total
						</ZText>
					</ZButton>
				)}
			</div>

			{retailInfo?.price && (
				<div className="mt-1 flex items-center">
					<ZText className="text-gray-7">
						{retailInfo.distributor ===
						Distributor.CustomDistributor
							? retailInfo.vendor_name
							: distributorLabel[retailInfo.distributor]}{" "}
						{ratailPriceLabel[retailInfo.distributor]}{" "}
						{currencyFormatter(retailInfo.price)}
					</ZText>
					<ZIcon
						onClick={updateSellPriceByRetailPrice}
						name={Icons.COPY}
						size="sm"
						className="mx-1 stroke-current text-blue-5 active:text-blue-6 cursor-pointer"
					/>
				</div>
			)}
		</div>
	);

	const SetupFee = ({ value, onChange: _onChange }: ICustomFormItemProps) => (
		<div className="flex items-center">
			<ZInputNumber
				type="number"
				step=".01"
				placeholder="Please enter product setup fee"
				min={0}
				className="mr-2"
				width={InputNumberWidths.lg}
				value={value}
				onChange={v => {
					// Don't call onChange(v) here to avoid double form update
					// onSetupPriceChange will handle the form update including the input value
					onSetupPriceChange(Number(v));
				}}
				precision={2}
				addOnAfter={getCurrencySettings().currency}
				disabled={isBundleEnhancementEnable && pricingDisabled}
			/>
			{isBundleProduct && (
				<ZButton
					className="sub-action-button m-0"
					type="link"
					onClick={updateSetupPriceFromBundleItems}
					disabled={isBundleEnhancementEnable && pricingDisabled}
				>
					<ZText className="text-blue-5 active:text-blue-6">
						<ZIcon
							name={Icons.COPY}
							size="sm"
							className="mx-1 stroke-current"
						/>
						Copy Product Total
					</ZText>
				</ZButton>
			)}
		</div>
	);

	const fields: IFormFieldProps[] = [
		{
			key: "selected_billing_period",
			label: "Billing Period",
			hasFeedback: true,
			widget: EWidgets.SELECT,
			disabled: isBundleEnhancementEnable && pricingDisabled,
			widgetProps: {
				selectOptions: (
					Object.keys(
						productBillingTypeOptions
					) as ProductBillingType[]
				).map(value => ({
					name: productBillingTypeOptions[value],
					value
				}))
			}
		},
		{
			key: "sell_price",
			label: "Sell Price",
			required: true,
			message: "Sell price cannot be empty",
			widget: SellPrice
		},
		{
			key: "selected_setup_price",
			label: "Setup Fee",
			required: true,
			message: "Product setup fee cannot be empty",
			widget: SetupFee
		}
	];

	if (drawerState !== EProductDrawerState.EDITORCLONE) {
		fields.splice(1, 0, {
			key: "quantity",
			label: "Quantity",
			required: true,
			message: "Please enter product quantity",
			hasFeedback: true,
			widget: EWidgets.INPUTNUMBER,
			disabled: isBundleEnhancementEnable && pricingDisabled,
			widgetProps: {
				width: InputNumberWidths.lg,
				placeholder: "Please enter product quantity",
				min: 1,
				precision: 0,
				onChange: onQuantityChange
			}
		});
	}

	return fields;
};
